import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import { updateUserDeletedReason } from '../utils/api';
import { useContentCatalog } from '../utils/contentCatalog';
import { SuccessToast, useSuccessToast } from './shared/SuccessToast';
import headerImage from '../assets/account-closed-header.png';
import styles from './AccountDeactivatedPage.module.css';

const AccountDeactivatedPage = () => {
  const navigate = useNavigate();
  const { catalog } = useContentCatalog();
  const deletedReasons = (catalog?.account_deletion_reasons || []).map((item) => item.label);
  const { message, isExiting, showToast } = useSuccessToast();
  const [token, setToken] = useState('');
  const [submittingReason, setSubmittingReason] = useState('');
  const [selectedReason, setSelectedReason] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    const storedToken = sessionStorage.getItem(config.STORAGE_KEYS.ACCOUNT_DEACTIVATED_TOKEN) || '';
    if (!storedToken) {
      navigate('/', { replace: true });
      return;
    }
    setToken(storedToken);
  }, [navigate]);

  const handleGoToWelcome = useCallback(() => {
    sessionStorage.removeItem(config.STORAGE_KEYS.ACCOUNT_DEACTIVATED_TOKEN);
    localStorage.clear();
    navigate('/');
  }, [navigate]);

  const handleReasonClick = useCallback(async (reason) => {
    if (!token || submittingReason) return;

    setSubmittingReason(reason);
    const result = await updateUserDeletedReason({ token, deletedReason: reason });
    setSubmittingReason('');

    if (result?.ok) {
      setSelectedReason(reason);
      showToast(result.message || 'Thank you for your feedback.');
      return;
    }

    showToast(result?.message || 'Failed to submit feedback. Please try again.');
  }, [token, submittingReason, showToast]);

  if (!token) return null;

  return (
    <div className={styles.page}>
      <SuccessToast message={message} isExiting={isExiting} />

      <header className={styles.siteHeader}>
        <h1 className={styles.brandTitle}>Richuel</h1>
        <p className={styles.brandTagline}>FINANCIAL WELLNESS</p>
      </header>

      <main className={styles.main}>
        <article className={styles.card}>
          <div className={styles.heroSection}>
            <div className={styles.heroWrap}>
              <img
                src={headerImage}
                alt=""
                className={styles.heroImage}
                aria-hidden="true"
              />
              <div className={styles.heroFade} aria-hidden="true" />
            </div>
            <div className={styles.checkIcon} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M7.5 12.5l3.5 3.5 5.5-5.5"
                  pathLength="1"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          <div className={styles.cardBody}>
            <h2 className={styles.title}>Your account has been deactivated</h2>
            <p className={styles.description}>
              We&apos;ve successfully processed your request. Your data is being handled according
              to our privacy policy. We&apos;re truly sorry to see you go, but we&apos;re grateful
              for the time you spent with us.
            </p>

            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleGoToWelcome}
            >
              Go to Welcome Page
            </button>
          </div>

          <div className={styles.infoBanner} role="note">
            <span className={styles.infoIcon} aria-hidden="true">i</span>
            <span className={styles.infoText}>Your data will be fully removed in 30 days.</span>
          </div>
        </article>

        <section className={styles.feedbackSection} aria-labelledby="feedback-heading">
          <p id="feedback-heading" className={styles.feedbackPrompt}>
            Have a moment to tell us why?
          </p>
          <div className={styles.reasonButtons} role="group" aria-label="Reason for leaving">
            {deletedReasons.map((reason) => {
              const isSelected = selectedReason === reason;
              const isSubmitting = submittingReason === reason;
              return (
                <button
                  key={reason}
                  type="button"
                  className={`${styles.reasonButton}${isSelected ? ` ${styles.reasonButtonSelected}` : ''}`}
                  onClick={() => handleReasonClick(reason)}
                  disabled={Boolean(submittingReason)}
                  aria-pressed={isSelected}
                  aria-busy={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : reason}
                </button>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
};

export default AccountDeactivatedPage;
